<template>
  <b-modal
    v-model="visible"
    title="Adjust photo"
    centered
    no-close-on-backdrop
    body-class="p-3"
    @hidden="onHidden"
  >
    <p class="text-muted small text-center mb-2">
      Drag to position, zoom with the slider (or pinch / mouse wheel). Fit the face inside the oval.
    </p>

    <div
      ref="viewport"
      class="pc-viewport mx-auto"
      :style="{ width: vw + 'px', height: vh + 'px' }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @pointerleave="onPointerUp"
      @wheel.prevent="onWheel"
    >
      <img
        v-if="loaded"
        :src="src"
        class="pc-image"
        draggable="false"
        alt=""
        :style="imageStyle"
      />
      <div v-else class="pc-loading">
        <span v-if="error" class="text-danger small px-3">{{ error }}</span>
        <span v-else class="spinner-border spinner-border-sm text-light"></span>
      </div>
      <div class="pc-guide"></div>
    </div>

    <div class="d-flex align-items-center mt-3" style="gap: 8px;">
      <button type="button" class="btn btn-sm btn-outline-secondary" title="Zoom out" @click="zoomBy(1 / 1.15)">
        <i class="fe fe-minus"></i>
      </button>
      <input
        type="range"
        class="custom-range flex-grow-1"
        min="1"
        :max="maxZoom"
        step="0.01"
        :value="zoom"
        @input="setZoom(parseFloat($event.target.value))"
      />
      <button type="button" class="btn btn-sm btn-outline-secondary" title="Zoom in" @click="zoomBy(1.15)">
        <i class="fe fe-plus"></i>
      </button>
    </div>

    <div class="d-flex justify-content-center mt-2" style="gap: 8px;">
      <button type="button" class="btn btn-sm btn-outline-secondary" @click="rotate(-90)">
        <i class="fe fe-rotate-ccw mr-1"></i> Rotate
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" @click="rotate(90)">
        <i class="fe fe-rotate-cw mr-1"></i> Rotate
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" @click="reset">
        <i class="fe fe-refresh-cw mr-1"></i> Reset
      </button>
    </div>

    <template #modal-footer>
      <button type="button" class="btn btn-outline-secondary" @click="finish(null)">Cancel</button>
      <button type="button" class="btn btn-primary" :disabled="!loaded" @click="usePhoto">
        <i class="fe fe-check mr-1"></i> Use photo
      </button>
    </template>
  </b-modal>
</template>

<script>
/**
 * Passport-style photo cropper. Usage:
 *   <PhotoCropper ref="photoCropper" />
 *   const dataUrl = await this.$refs.photoCropper.open(src); // null when cancelled
 * `src` may be a data URL, blob: URL or same-origin URL (e.g. a Capacitor webPath).
 * The result is a JPEG data URL of outputWidth x outputWidth*aspect pixels.
 */
export default {
  name: "PhotoCropper",
  props: {
    // height / width of the frame; 45 / 35 is a passport photo
    aspect: { type: Number, default: 45 / 35 },
    outputWidth: { type: Number, default: 420 },
    quality: { type: Number, default: 0.85 },
    maxZoom: { type: Number, default: 5 },
  },
  data() {
    return {
      visible: false,
      src: "",
      loaded: false,
      error: "",
      naturalW: 0,
      naturalH: 0,
      zoom: 1, // 1 = image just covers the frame
      rotation: 0, // 0, 90, 180, 270
      x: 0, // image centre offset from frame centre, in frame pixels
      y: 0,
      vw: 280,
      pointers: {},
      gesture: null,
    };
  },
  computed: {
    vh() {
      return Math.round(this.vw * this.aspect);
    },
    rotatedW() {
      return this.rotation % 180 ? this.naturalH : this.naturalW;
    },
    rotatedH() {
      return this.rotation % 180 ? this.naturalW : this.naturalH;
    },
    minScale() {
      if (!this.naturalW) return 1;
      return Math.max(this.vw / this.rotatedW, this.vh / this.rotatedH);
    },
    scale() {
      return this.minScale * this.zoom;
    },
    imageStyle() {
      return {
        width: this.naturalW * this.scale + "px",
        height: this.naturalH * this.scale + "px",
        transform: `translate(-50%, -50%) translate(${this.x}px, ${this.y}px) rotate(${this.rotation}deg)`,
      };
    },
  },
  methods: {
    open(src) {
      this.src = src;
      this.loaded = false;
      this.error = "";
      this.rotation = 0;
      this.zoom = 1;
      this.x = 0;
      this.y = 0;
      this.pointers = {};
      this.gesture = null;
      this.vw = Math.min(300, Math.max(200, window.innerWidth - 80));
      this.visible = true;

      const img = new Image();
      img.onload = () => {
        this.naturalW = img.naturalWidth;
        this.naturalH = img.naturalHeight;
        this.imageEl = img;
        this.loaded = true;
      };
      img.onerror = () => {
        this.error = "Could not load this image. Try another photo.";
      };
      img.src = src;

      return new Promise((resolve) => {
        this.resolver = resolve;
      });
    },
    finish(result) {
      if (this.resolver) {
        this.resolver(result);
        this.resolver = null;
      }
      this.visible = false;
    },
    onHidden() {
      // closed with Esc or the header ×
      this.finish(null);
      this.imageEl = null;
    },

    clamp() {
      const maxX = Math.max(0, (this.rotatedW * this.scale - this.vw) / 2);
      const maxY = Math.max(0, (this.rotatedH * this.scale - this.vh) / 2);
      this.x = Math.min(maxX, Math.max(-maxX, this.x));
      this.y = Math.min(maxY, Math.max(-maxY, this.y));
    },
    setZoom(z) {
      const next = Math.min(this.maxZoom, Math.max(1, z));
      const ratio = next / this.zoom;
      this.x *= ratio; // zoom around the frame centre
      this.y *= ratio;
      this.zoom = next;
      this.clamp();
    },
    zoomBy(factor) {
      this.setZoom(this.zoom * factor);
    },
    rotate(deg) {
      this.rotation = (this.rotation + deg + 360) % 360;
      const { x, y } = this;
      // keep the same part of the photo in the middle
      if (deg > 0) {
        this.x = -y;
        this.y = x;
      } else {
        this.x = y;
        this.y = -x;
      }
      this.clamp();
    },
    reset() {
      this.rotation = 0;
      this.zoom = 1;
      this.x = 0;
      this.y = 0;
    },

    onWheel(e) {
      if (!this.loaded) return;
      this.zoomBy(Math.exp(-e.deltaY * 0.0015));
    },
    onPointerDown(e) {
      if (!this.loaded) return;
      this.$refs.viewport.setPointerCapture && this.$refs.viewport.setPointerCapture(e.pointerId);
      this.$set(this.pointers, e.pointerId, { x: e.clientX, y: e.clientY });
      this.startGesture();
    },
    onPointerMove(e) {
      if (!this.pointers[e.pointerId] || !this.gesture) return;
      this.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      const g = this.gesture;
      const pts = Object.values(this.pointers);
      if (pts.length >= 2 && g.distance) {
        this.setZoom(g.zoom * (this.distance(pts) / g.distance));
      }
      const c = this.centre(pts);
      this.x += c.x - g.last.x;
      this.y += c.y - g.last.y;
      g.last = c;
      this.clamp();
    },
    onPointerUp(e) {
      if (!this.pointers[e.pointerId]) return;
      this.$delete(this.pointers, e.pointerId);
      this.startGesture();
    },
    startGesture() {
      const pts = Object.values(this.pointers);
      this.gesture = pts.length
        ? { last: this.centre(pts), zoom: this.zoom, distance: pts.length >= 2 ? this.distance(pts) : 0 }
        : null;
    },
    centre(pts) {
      return {
        x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
        y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
      };
    },
    distance(pts) {
      return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
    },

    usePhoto() {
      const result = this.crop();
      if (result) this.finish(result);
    },
    crop() {
      const outW = this.outputWidth;
      const outH = Math.round(outW * this.aspect);
      const k = outW / this.vw;
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, outW, outH);
      ctx.imageSmoothingQuality = "high";
      ctx.translate(outW / 2 + this.x * k, outH / 2 + this.y * k);
      ctx.rotate((this.rotation * Math.PI) / 180);
      const w = this.naturalW * this.scale * k;
      const h = this.naturalH * this.scale * k;
      ctx.drawImage(this.imageEl, -w / 2, -h / 2, w, h);
      try {
        return canvas.toDataURL("image/jpeg", this.quality);
      } catch (err) {
        this.error = "This image cannot be cropped here. Try another photo.";
        return null;
      }
    },
  },
};
</script>

<style scoped>
.pc-viewport {
  position: relative;
  overflow: hidden;
  background: #1f2937;
  border-radius: 8px;
  touch-action: none;
  user-select: none;
  cursor: grab;
}
.pc-viewport:active {
  cursor: grabbing;
}
.pc-image {
  position: absolute;
  left: 50%;
  top: 50%;
  max-width: none;
  pointer-events: none;
}
.pc-loading {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
/* face guide: an oval for the head, the rest of the frame stays visible */
.pc-guide {
  position: absolute;
  left: 20%;
  right: 20%;
  top: 10%;
  height: 62%;
  border: 2px dashed rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}
</style>
